import { useEffect, useMemo, useRef } from 'react';
import { Box } from '@withjoy/joykit';
import { animated, useSpring } from 'react-spring';
import type { SpringConfig } from 'react-spring';
import { withWindow } from '@shared/utils/withWindow';
import type { DrawerAnimationStyle, UseDrawerAnimationParams } from './drawerAnimationBase';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useRequestAnimationFrame } from '@shared/utils/hooks/useRequestAnimationFrame';

const NATURAL_SPRING_CONFIG: SpringConfig = {
  mass: 1.05,
  tension: 220,
  friction: 28,
  clamp: false,
  precision: 0.05,
};

const AnimatedBoxSpring = animated(Box);

const useDrawerSpringStyle = ({ isOpen, openPositionVh, openPositionOffsetPx, closedHeightPx }: UseDrawerAnimationParams): DrawerAnimationStyle => {
  const viewportHeightRef = useRef(
    withWindow(global => {
      return global.visualViewport?.height ?? global.innerHeight;
    }, 0),
  );
  const scheduleAnimationFrame = useRequestAnimationFrame();

  const getTargetTranslateY = useEventCallback((open: boolean) => {
    const vh = viewportHeightRef.current;
    const openPositionPx = (vh * openPositionVh) / 100 + openPositionOffsetPx;
    const closedPositionPx = Math.max(0, vh - closedHeightPx);
    return open ? openPositionPx : closedPositionPx;
  });

  const [spring, setSpring] = useSpring(() => ({
    translateY: getTargetTranslateY(isOpen),
    config: NATURAL_SPRING_CONFIG,
    // skip animtion for SSR, i.e. don't try to access window
    immediate: withWindow(() => false, true),
  }));

  useEffect(() => {
    setSpring({ translateY: getTargetTranslateY(isOpen) });
  }, [isOpen, openPositionVh, openPositionOffsetPx, closedHeightPx, setSpring, getTargetTranslateY]);

  useEffect(
    () =>
      withWindow(
        global => {
          // setSpring debounces calls internally,
          // but we could add a debounce here if we notice performance issues
          const updateViewportHeight = () => {
            const nextHeight = global.visualViewport?.height ?? global.innerHeight;
            if (viewportHeightRef.current !== nextHeight) {
              viewportHeightRef.current = nextHeight;
              setSpring({ translateY: getTargetTranslateY(isOpen) });
            }
          };

          updateViewportHeight();
          // Safari route transitions leave viewport metrics stale for one frame; the second reads correct dimensions.
          const cancelDeferredViewportSync = scheduleAnimationFrame(updateViewportHeight, 2);
          global.addEventListener('resize', updateViewportHeight);
          global.addEventListener('orientationchange', updateViewportHeight);

          return () => {
            cancelDeferredViewportSync();
            global.removeEventListener('resize', updateViewportHeight);
            global.removeEventListener('orientationchange', updateViewportHeight);
          };
        },
        () => {},
      ),
    [isOpen, scheduleAnimationFrame, setSpring, getTargetTranslateY],
  );

  return useMemo(
    () => ({
      transform: spring.translateY.interpolate(value => `translateY(${value}px)`),
      willChange: 'transform',
    }),
    [spring],
  );
};

export const springAnimation = {
  Wrapper: AnimatedBoxSpring,
  useAnimation: useDrawerSpringStyle,
} as const;
